/*
 * Copyright © 2026 MS IMAGES. All rights reserved.
 * Proprietary and confidential — unauthorized copying, distribution, or use
 * of this file, via any medium, is strictly prohibited. See LICENSE.
 */

/**
 * ============================================================================
 * Invite Codes Hooks - React Query for Invite Codes
 * ============================================================================
 *
 * Custom hooks for invite code data fetching and mutations using React Query.
 *
 * ============================================================================
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { inviteCodesService } from '@/services/superadmin/invite-codes.service';
import { InviteCode, CreateInviteCodeRequest, InviteCodeStats } from '@/types/superadmin.types';
import { ApiError, PaginatedResponse, PaginationParams } from '@/services/api/types';

// Query keys for cache management
export const inviteCodeKeys = {
  all: ['inviteCodes'] as const,
  lists: () => [...inviteCodeKeys.all, 'list'] as const,
  list: (params?: PaginationParams) => [...inviteCodeKeys.lists(), params] as const,
  details: () => [...inviteCodeKeys.all, 'detail'] as const,
  detail: (id: string) => [...inviteCodeKeys.details(), id] as const,
  stats: () => [...inviteCodeKeys.all, 'stats'] as const,
};

/**
 * Hook to fetch paginated invite codes
 */
export function useInviteCodes(params?: PaginationParams) {
  return useQuery<PaginatedResponse<InviteCode>, ApiError>({
    queryKey: inviteCodeKeys.list(params),
    queryFn: () => inviteCodesService.getInviteCodes(params),
    staleTime: 30 * 1000,
  });
}

/**
 * Hook to fetch single invite code detail
 */
export function useInviteCode(id: string) {
  return useQuery<InviteCode, ApiError>({
    queryKey: inviteCodeKeys.detail(id),
    queryFn: () => inviteCodesService.getInviteCode(id),
    enabled: !!id,
  });
}

/**
 * Hook to fetch invite code statistics
 */
export function useInviteCodeStats() {
  return useQuery<InviteCodeStats, ApiError>({
    queryKey: inviteCodeKeys.stats(),
    queryFn: () => inviteCodesService.getInviteCodeStats(),
    staleTime: 60 * 1000,
  });
}

/**
 * Hook for creating invite code
 */
export function useCreateInviteCode() {
  const queryClient = useQueryClient();

  return useMutation<InviteCode, ApiError, CreateInviteCodeRequest>({
    mutationFn: inviteCodesService.createInviteCode,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: inviteCodeKeys.lists() });
      queryClient.invalidateQueries({ queryKey: inviteCodeKeys.stats() });
    },
  });
}

/**
 * Hook for disabling invite code
 */
export function useDisableInviteCode() {
  const queryClient = useQueryClient();

  return useMutation<void, ApiError, string>({
    mutationFn: inviteCodesService.disableInviteCode,
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({ queryKey: inviteCodeKeys.detail(id) });
      queryClient.invalidateQueries({ queryKey: inviteCodeKeys.lists() });
      queryClient.invalidateQueries({ queryKey: inviteCodeKeys.stats() });
    },
  });
}

/**
 * Hook for activating invite code
 */
export function useActivateInviteCode() {
  const queryClient = useQueryClient();

  return useMutation<void, ApiError, string>({
    mutationFn: inviteCodesService.activateInviteCode,
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({ queryKey: inviteCodeKeys.detail(id) });
      queryClient.invalidateQueries({ queryKey: inviteCodeKeys.lists() });
      queryClient.invalidateQueries({ queryKey: inviteCodeKeys.stats() });
    },
  });
}
